import { clientApiScope, Constants } from "../../../services/web/api.const";
import { useApi, useApiAsync } from "../../../services/web/use-api";
import { ParkingRateStatusEnum, ParkingRateType, PricingPointType } from "../payment-zone-area.type";
import { RateSimulationRequest, RateSimulationResult } from "../rate-simulation.types";

export const useGetParkingRates = (cityId: string) => {
  const enabled = !!cityId;
  const url = `${Constants.CLIENTS_API_URL}/parking-rates/${cityId}`;
  const options = {
    ...clientApiScope,
    method: "GET",
    headers: {
      "Content-type": "application/json; charset=UTF-8",
      "api-version": "v2",
    },
  };

  const {
    isLoading: loading,
    error,
    data: parkingRates,
  } = useApi<ParkingRateType[]>(url, options, ["parking-rates", cityId], enabled);

  return {
    loading,
    error,
    parkingRates: parkingRates ?? [],
  };
};

export const useCreateParkingRate = () => {
  const url = `${Constants.CLIENTS_API_URL}/parking-rates`;

  const options = {
    ...clientApiScope,
    method: "POST",
    headers: {
      "Content-type": "application/json; charset=UTF-8",
      "api-version": "v2",
    },
  };

  const {
    mutateAsync: createParkingRate,
    isPending: loading,
    error,
  } = useApiAsync<void, ParkingRateType>(url, options, ["parking-rates"]);

  return {
    createParkingRate,
    loading,
    error,
  };
};

export const useUpdateParkingRate = () => {
  const url = `${Constants.CLIENTS_API_URL}/parking-rates`;

  const options = {
    ...clientApiScope,
    method: "PUT",
    headers: {
      "Content-type": "application/json; charset=UTF-8",
      "api-version": "v2",
    },
  };

  const {
    mutateAsync: updateParkingRate,
    isPending: loading,
    error,
  } = useApiAsync<void, ParkingRateType>(url, options, ["parking-rates"]);

  return {
    updateParkingRate,
    loading,
    error,
  };
};

export const usePublishParkingRate = () => {
  const options = {
    ...clientApiScope,
    method: "PUT",
    headers: {
      "Content-type": "application/json; charset=UTF-8",
      "api-version": "v2",
    },
    toBody: (vars: { id: string; compiledPricingPoints: PricingPointType[] }) => ({
      compiledPricingPoints: vars.compiledPricingPoints,
    }),
  };

  const {
    mutateAsync: publishParkingRate,
    isPending: loading,
    error,
  } = useApiAsync<void, { id: string; compiledPricingPoints: PricingPointType[] }>(
    (vars) => `${Constants.CLIENTS_API_URL}/parking-rates/${vars.id}/publish`,
    options,
    ["parking-rates"],
  );

  return {
    publishParkingRate,
    loading,
    error,
  };
};

export const useSetParkingRateState = () => {
  const options = {
    ...clientApiScope,
    method: "PUT",
    headers: {
      "Content-type": "application/json; charset=UTF-8",
      "api-version": "v2",
    },
    toBody: (vars: { id: string; status: ParkingRateStatusEnum }) => ({ status: vars.status }),
  };

  const {
    mutateAsync: setParkingRateState,
    isPending: loading,
    error,
  } = useApiAsync<void, { id: string; status: ParkingRateStatusEnum }>(
    (vars) => `${Constants.CLIENTS_API_URL}/parking-rates/${vars.id}/set-state`,
    options,
    ["parking-rates"],
  );

  return { setParkingRateState, loading, error };
};

export const useSimulateParkingRate = (parkingRateId: string) => {
  const url = `${Constants.CLIENTS_API_URL}/parking-rates/${parkingRateId}/simulate`;

  const options = {
    ...clientApiScope,
    method: "POST",
    headers: {
      "Content-type": "application/json; charset=UTF-8",
      "api-version": "v2",
    },
  };

  const {
    mutateAsync: simulateParkingRate,
    isPending: loading,
    error,
  } = useApiAsync<RateSimulationResult, RateSimulationRequest>(url, options, ["rate-simulation", parkingRateId]);

  return {
    simulateParkingRate,
    loading,
    error,
  };
};

export const useRemoveParkingRate = () => {
  const options = {
    ...clientApiScope,
    method: "DELETE",
    headers: {
      "Content-type": "application/json; charset=UTF-8",
      "api-version": "v2",
    },
    toBody: () => undefined,
  };

  const {
    mutateAsync: removeParkingRate,
    isPending: loading,
    error,
  } = useApiAsync<void, string>(
    (parkingRateId) => `${Constants.CLIENTS_API_URL}/parking-rates/${parkingRateId}`,
    options,
    ["parking-rates"],
  );

  return {
    removeParkingRate,
    loading,
    error,
  };
};
